import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

const DEFAULT_LIMIT = 20;

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const navigate = useNavigate();

  const [admin, setAdmin] = useState(() => {
    const saved = localStorage.getItem('admin_session');
    return saved ? JSON.parse(saved) : null;
  });

  const [userSearch, setUserSearch] = useState('');
  const [userPlan, setUserPlan] = useState('All');
  const [userFilter, setUserFilter] = useState('active');
  const [activeView, setActiveView] = useState(() => {
    const saved = localStorage.getItem('admin_session');
    return saved ? 'dashboard' : 'login';
  });

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api/admin';

  // 401 = session gone (expired, disabled or deleted account) -> back to login.
  // 403 = signed in but no access to that section -> the caller shows its own error.
  const apiFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, { ...options, credentials: 'include' });
    if (res.status === 401) {
      localStorage.removeItem('admin_session');
      setAdmin(null);
      setActiveView('login');
      navigate('/login', { replace: true });
      return null;
    }
    return res;
  }, [navigate]);

  // Role/permissions can change while someone is signed in (and sessions saved before roles
  // existed have none), so refresh them from the server on load.
  useEffect(() => {
    if (!admin) return;
    let cancelled = false;
    apiFetch(`${API_BASE}/me`)
      .then((res) => (res && res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.admin) return;
        localStorage.setItem('admin_session', JSON.stringify(data.admin));
        setAdmin(data.admin);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOwner = admin?.role === 'owner' || (!!admin && !admin.role);
  const can = useCallback(
    (permission) => !!admin && (isOwner || (admin.permissions || []).includes(permission)),
    [admin, isOwner],
  );

  const queryClient = useQueryClient();

  // TanStack Query handles the caching: same search/plan/filter + page within staleTime is served
  // from cache with no network call, and pages already fetched survive unmount/remount
  // (e.g. navigating into a user's detail page and back) instead of resetting to page 1.
  const usersQuery = useInfiniteQuery({
    queryKey: ['admin-users', userSearch, userPlan, userFilter],
    queryFn: async ({ pageParam }) => {
      const queryParams = new URLSearchParams();
      if (userSearch) queryParams.append('search', userSearch);
      if (userPlan && userPlan !== 'All') queryParams.append('plan', userPlan);
      if (userFilter) queryParams.append('filter', userFilter);
      queryParams.append('page', pageParam);
      queryParams.append('limit', DEFAULT_LIMIT);

      const res = await apiFetch(`${API_BASE}/users?${queryParams.toString()}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res || !res.ok) throw new Error('Failed to fetch users');
      return res.json();
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined),
    enabled: !!admin && can('users'),
  });

  const users = useMemo(() => {
    const pages = usersQuery.data?.pages || [];
    const map = new Map();
    pages.forEach(p => (p.users || []).forEach(u => map.set(u.id, u)));
    return Array.from(map.values());
  }, [usersQuery.data]);

  const pagination = useMemo(() => {
    const pages = usersQuery.data?.pages || [];
    const last = pages[pages.length - 1];
    return {
      page: last?.page || 1,
      limit: last?.limit || DEFAULT_LIMIT,
      total: last?.total || users.length,
      totalPages: last?.totalPages || 1,
    };
  }, [usersQuery.data, users.length]);

  const loading = usersQuery.isLoading;
  const loadingMore = usersQuery.isFetchingNextPage;
  const hasMoreUsers = !!usersQuery.hasNextPage;

  const loadMoreUsers = useCallback(() => {
    if (usersQuery.hasNextPage && !usersQuery.isFetchingNextPage) {
      usersQuery.fetchNextPage();
    }
  }, [usersQuery]);

  // Global platform stats for the "Users Analytics Directory" cards - a separate, unpaginated
  // call to GET /users/stats (the old embedded per-page `summary` block is gone).
  const usersStatsQuery = useQuery({
    queryKey: ['admin-users-stats'],
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/users/stats`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res || !res.ok) throw new Error('Failed to fetch user stats');
      return res.json();
    },
    enabled: !!admin && can('users'),
  });

  const usersStats = usersStatsQuery.data || {
    totalUsers: pagination.total,
    installedUsers: 0,
    freeTierUsers: 0,
    subscriptionTiers: { premium: 0, pro: 0, total: 0 },
  };

  // Invalidate the cache and refetch fresh - used after a mutation changes user/table data
  const refreshUsers = useCallback(() => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
      queryClient.invalidateQueries({ queryKey: ['admin-users-stats'] }),
    ]);
  }, [queryClient]);

  // Invalidate a single user's manage-page detail (GET /users/:userId) after a mutation on them
  const refreshUserDetail = useCallback((userId) => {
    return queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
  }, [queryClient]);

  const login = async (email, password) => {
    try {
      // Plain fetch, not apiFetch: a 401 here means a wrong password, not an expired session.
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        // The password was right, but the session lives in a cookie for the API's domain. When the
        // panel runs on another site (e.g. localhost against api-qa.ryzz.gg) the browser may block it
        // as a third-party cookie (always in incognito), and the next request would bounce to login.
        const check = await fetch(`${API_BASE}/me`, { credentials: 'include' }).catch(() => null);
        if (!check || check.status === 401) {
          return {
            success: false,
            error: 'Your password is correct, but this browser blocked the login cookie (third-party cookies are blocked, e.g. in an incognito window). Use a normal window, allow cookies for this site, or open the admin panel on the same domain as the API.',
          };
        }
        localStorage.setItem('admin_session', JSON.stringify(data.admin));
        setAdmin(data.admin);
        setActiveView('dashboard');
        return { success: true };
      }
      return { success: false, error: data.error || 'Invalid credentials' };
    } catch (err) {
      return { success: false, error: 'Cannot connect to backend server.' };
    }
  };

  const logout = async () => {
    try {
      await apiFetch(`${API_BASE}/logout`, { method: 'POST' });
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('admin_session');
    setAdmin(null);
    setActiveView('login');
    queryClient.removeQueries({ queryKey: ['admin-users'] });
    queryClient.removeQueries({ queryKey: ['admin-users-stats'] });
    queryClient.removeQueries({ queryKey: ['admin-user'] });
    queryClient.removeQueries({ queryKey: ['admin-team'] });
  };

  const addCredits = async (userId, amount) => {
    try {
      const res = await apiFetch(`${API_BASE}/users/${userId}/credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount }),
      });
      if (res && res.ok) await Promise.all([refreshUsers(), refreshUserDetail(userId)]);
    } catch (err) {
      console.error(err);
    }
  };

  const changePlan = async (userId, newPlan) => {
    try {
      const res = await apiFetch(`${API_BASE}/users/${userId}/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: newPlan }),
      });
      if (res && res.ok) await Promise.all([refreshUsers(), refreshUserDetail(userId)]);
    } catch (err) {
      console.error(err);
    }
  };

  const changeServerPlan = async (userId, serverId, newPlan) => {
    try {
      const res = await apiFetch(`${API_BASE}/users/${userId}/servers/${serverId}/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: newPlan }),
      });
      if (res && res.ok) await Promise.all([refreshUsers(), refreshUserDetail(userId)]);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleStatus = async (userId) => {
    try {
      const res = await apiFetch(`${API_BASE}/users/${userId}/toggle-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res && res.ok) await Promise.all([refreshUsers(), refreshUserDetail(userId)]);
    } catch (err) {
      console.error(err);
    }
  };

  // Cycle bot status: Online -> Offline -> Maintenance
  const toggleServerStatus = async (userId, serverId) => {
    try {
      const res = await apiFetch(`${API_BASE}/users/${userId}/servers/${serverId}/toggle-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res && res.ok) await Promise.all([refreshUsers(), refreshUserDetail(userId)]);
    } catch (err) {
      console.error(err);
    }
  };

  // Reboot a server's bot connection
  const rebootServer = async (userId, serverId) => {
    try {
      const res = await apiFetch(`${API_BASE}/users/${userId}/servers/${serverId}/reboot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res && res.ok) await Promise.all([refreshUsers(), refreshUserDetail(userId)]);
    } catch (err) {
      console.error(err);
    }
  };

  // Process all pending characters on a server
  const processPendingCharacters = async (userId, serverId) => {
    try {
      const res = await apiFetch(`${API_BASE}/users/${userId}/servers/${serverId}/process-pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res && res.ok) await Promise.all([refreshUsers(), refreshUserDetail(userId)]);
    } catch (err) {
      console.error(err);
    }
  };

  // Reset all characters used on a server
  const resetCharacters = async (userId, serverId) => {
    try {
      const res = await apiFetch(`${API_BASE}/users/${userId}/servers/${serverId}/reset-characters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res && res.ok) await Promise.all([refreshUsers(), refreshUserDetail(userId)]);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AdminContext.Provider value={{
      admin,
      isOwner,
      can,
      API_BASE,
      apiFetch,
      users,
      loading,
      loadingMore,
      pagination,
      usersStats,
      userSearch,
      setUserSearch,
      userPlan,
      setUserPlan,
      userFilter,
      setUserFilter,
      activeView,
      setActiveView,
      login,
      logout,
      addCredits,
      changePlan,
      changeServerPlan,
      toggleStatus,
      toggleServerStatus,
      rebootServer,
      processPendingCharacters,
      resetCharacters,
      hasMoreUsers,
      loadMoreUsers,
      refreshUsers,
      refreshUserDetail,
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
